import { useDynamicTitle } from "@/hooks/use-dynamic-title";
import { useEffect, type FC } from "react";
import { Heading1 } from "@/components/podkit/typography/Headings";
import { useNavigate, useParams } from "react-router-dom";
import { useGetAgentExecution, type PlainAgentExecution } from "@/queries/agent-queries";
import { ErrorMessage } from "@/components/ErrorMessage";
import { SkeletonText } from "@/components/podkit/loading/Skeleton";
import { Code, ConnectError } from "@connectrpc/connect";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { Card } from "@/components/flexkit/Card";
import { Text } from "@/components/podkit/typography/Text";
import { Button } from "@/components/flexkit/Button";
import { IconAI } from "@/assets/icons/geist/IconAI";
import { Link } from "@/components/flexkit/Link";
import { AgentExecutionConversation } from "@/components/ai/chat/AgentExecutionConversation";
import { getDetailsURL } from "@/routes/environments/details-url";
import { getActiveEnvironmentId } from "@/utils/agent-executions";

export const AgentExecutionPage: FC = () => {
    useDynamicTitle("Conversation");
    const { agentExecutionId } = useParams();
    const { data: agentExecution, isPending, error } = useGetAgentExecution(agentExecutionId);
    const navigate = useNavigate();

    const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;
    const isError = error instanceof ConnectError && error.code !== Code.NotFound;

    useEffect(() => {
        const activeEnvironmentId = getActiveEnvironmentId(agentExecution);
        if (activeEnvironmentId) {
            navigate(getDetailsURL({ environment: { id: activeEnvironmentId } }));
        }
    }, [agentExecution, agentExecutionId, navigate]);

    return (
        <div data-testid="agent-execution-page" className="mx-auto flex h-full flex-col gap-6">
            {isPending && <AgentExecutionPending />}
            {isNotFound && <AgentExecution404 agentExecutionId={agentExecutionId} />}
            {isError && <AgentExecutionError error={error} />}
            {agentExecution && <AgentExecutionContent agentExecution={agentExecution} />}
        </div>
    );
};

const AgentExecution404: FC<{ agentExecutionId?: string }> = () => {
    return (
        <Card
            data-testid="agent-execution-404"
            className="m-6 flex h-full max-h-[400px] min-h-0 flex-col justify-center overflow-y-hidden px-0.5 py-1"
            variant="bordered"
        >
            <ScrollArea orientation="vertical">
                <div className="flex flex-col items-center gap-8 py-5">
                    <div className="flex flex-col items-center gap-1">
                        <IconAI size="lg" className="size-10" />
                        <Heading1>Chat not found</Heading1>

                        <Text className="text-center text-xl font-semibold text-content-secondary">
                            The conversation you are looking for cannot be found.
                        </Text>
                    </div>
                    <Button data-testid="agent-execution-404-start-new-chat-button" asChild>
                        <Link to="/ai">Start a New Chat</Link>
                    </Button>
                </div>
            </ScrollArea>
        </Card>
    );
};

const AgentExecutionPending: FC = () => {
    return (
        <div data-testid="agent-execution-pending" className="p-6">
            <SkeletonText size={"lg"} ready={false} className="w-[200px]" />
        </div>
    );
};

const AgentExecutionError: FC<{ error: Error }> = ({ error }) => {
    return (
        <div data-testid="agent-execution-error" className="m-6">
            <Heading1 className="text-xl">Conversation failed to load</Heading1>
            <ErrorMessage error={error} />
        </div>
    );
};

const AgentExecutionContent: FC<{ agentExecution: PlainAgentExecution }> = ({ agentExecution }) => {
    return (
        <div data-testid="agent-execution-content" className="contents">
            <AgentExecutionConversation key={agentExecution.id} agentExecution={agentExecution} />
        </div>
    );
};
